/**
 * AlertRuleForm.tsx
 * Feature: 125 - Alert Rule Management
 * Author: Peter Dobler
 * Date: 2026-03-25
 * Constitution Version: 1.12.0
 *
 * Purpose: Modal form for creating and editing alert rules.
 * Supports all CRUD operations with validation.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Save, AlertTriangle } from 'lucide-react';
import { Modal, Button } from '../../../components/ui';
import {
  useCreateAlertRule,
  useUpdateAlertRule,
  useSeverities,
  useValidateAlertRuleName,
} from '../../../hooks/useAlertRules';
import useLakehouses, { LakehouseWithWorkspace } from '../../../modules/backup/hooks/useLakehouses';
import type {
  AlertRuleView,
  AlertType,
  Severity,
  ResourceType,
  ResourceTypeOption,
  NotificationChannel,
  ConditionJson,
  CreateAlertRuleRequest,
  UpdateAlertRuleRequest,
  SeverityOption,
} from '@shared/types/alertRuleManagement';
import { RESOURCE_TYPE_OPTIONS } from '@shared/types/alertRuleManagement';
import { NotificationChannelEditor } from './components/NotificationChannelEditor';

// =============================================================================
// Types
// =============================================================================

export interface AlertRuleFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingRule?: AlertRuleView | null;
}

interface FormData {
  name: string;
  resourceType: ResourceType; // Spec 193: selects the engine adapter (condition_json.resource_type)
  alertType: AlertType; // free display label (auto-derived from resourceType, editable)
  conditionJson: string;
  lakehouseId: string;
  enabled: boolean;
  severityOverride: Severity | '';
  notificationChannels: NotificationChannel[];
  cooldownMinutes: number;
}

/** Working starter condition per resource_type (the user refines adapter-specifics). */
const CONDITION_TEMPLATES: Partial<Record<ResourceType, Record<string, unknown>>> = {
  'backup-readiness': { resource_type: 'backup-readiness', status: ['at_risk', 'blocked'] },
  'backup-run': { resource_type: 'backup-run' },
  'capacity': { resource_type: 'capacity' },
  'storage-threshold': { resource_type: 'storage-threshold' },
};
function conditionTemplateFor(rt: ResourceType): Record<string, unknown> {
  return CONDITION_TEMPLATES[rt] ?? { resource_type: rt };
}
/** A default display label derived from a resource_type (e.g. 'backup-run' → 'Backup Run'). */
function deriveAlertType(rt: ResourceType): string {
  return rt.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join('');
}

interface FormErrors {
  name?: string;
  alertType?: string;
  conditionJson?: string;
  cooldownMinutes?: string;
  notificationChannels?: string;
  general?: string;
}

// =============================================================================
// Constants
// =============================================================================

const DEFAULT_RESOURCE_TYPE: ResourceType = 'pipeline';
const DEFAULT_FORM_DATA: FormData = {
  name: '',
  resourceType: DEFAULT_RESOURCE_TYPE,
  alertType: deriveAlertType(DEFAULT_RESOURCE_TYPE),
  conditionJson: JSON.stringify(conditionTemplateFor(DEFAULT_RESOURCE_TYPE), null, 2),
  lakehouseId: '',
  enabled: true,
  severityOverride: '',
  notificationChannels: [],
  cooldownMinutes: 60,
};

// =============================================================================
// Component
// =============================================================================

export const AlertRuleForm: React.FC<AlertRuleFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editingRule,
}) => {
  const isEditing = !!editingRule;

  // Form state
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameCheckPending, setNameCheckPending] = useState(false);

  // Queries
  const { data: severities } = useSeverities();
  const { data: lakehouses } = useLakehouses();
  const validateNameMutation = useValidateAlertRuleName();

  // Mutations
  const createMutation = useCreateAlertRule();
  const updateMutation = useUpdateAlertRule();

  // Initialize form when editing
  useEffect(() => {
    if (isOpen) {
      if (editingRule) {
        const rt = (editingRule.conditionJson as { resource_type?: string } | null)?.resource_type;
        setFormData({
          name: editingRule.name,
          resourceType: (rt as ResourceType) || DEFAULT_RESOURCE_TYPE,
          alertType: editingRule.alertType,
          conditionJson: JSON.stringify(editingRule.conditionJson, null, 2),
          lakehouseId: editingRule.lakehouseId || '',
          enabled: editingRule.enabled,
          severityOverride: editingRule.severityOverride || '',
          notificationChannels: editingRule.notificationChannels || [],
          cooldownMinutes: editingRule.cooldownMinutes,
        });
      } else {
        setFormData(DEFAULT_FORM_DATA);
      }
      setErrors({});
    }
  }, [isOpen, editingRule]);

  // Validate JSON — Spec 193: an engine condition must be a JSON object carrying
  // `resource_type` (the adapter key). Adapter-specific fields are free-form.
  const validateConditionJson = useCallback((json: string): string | null => {
    try {
      const parsed = JSON.parse(json);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return 'Condition must be a JSON object';
      }
      if (!parsed.resource_type) {
        return 'Condition must include a "resource_type" (set by the Resource Type selector)';
      }
      return null;
    } catch {
      return 'Invalid JSON syntax';
    }
  }, []);

  // Selecting a resource type sets the adapter key everywhere: condition_json.resource_type,
  // a starter condition template, and a default alert-type label.
  const handleResourceTypeChange = useCallback(
    (rt: ResourceType) => {
      setFormData((prev) => {
        let condition: Record<string, unknown>;
        try {
          const parsed = JSON.parse(prev.conditionJson) as Record<string, unknown>;
          // keep the user's edits but re-point resource_type; if it was a bare template, refresh it
          const wasTemplate = JSON.stringify(parsed) === JSON.stringify(conditionTemplateFor(prev.resourceType));
          condition = wasTemplate ? conditionTemplateFor(rt) : { ...parsed, resource_type: rt };
        } catch {
          condition = conditionTemplateFor(rt);
        }
        const derivedLabel = deriveAlertType(prev.resourceType);
        return {
          ...prev,
          resourceType: rt,
          // only overwrite the label if the user hadn't customised it
          alertType: !prev.alertType || prev.alertType === derivedLabel ? deriveAlertType(rt) : prev.alertType,
          conditionJson: JSON.stringify(condition, null, 2),
        };
      });
    },
    []
  );

  // Validate form
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 255) {
      newErrors.name = 'Name must be 255 characters or less';
    }

    // Condition JSON validation
    const jsonError = validateConditionJson(formData.conditionJson);
    if (jsonError) {
      newErrors.conditionJson = jsonError;
    }

    // Cooldown validation
    if (formData.cooldownMinutes < 1 || formData.cooldownMinutes > 1440) {
      newErrors.cooldownMinutes = 'Cooldown must be between 1 and 1440 minutes';
    }

    // Notification channel validation
    const invalidChannels = formData.notificationChannels.some(
      (ch) => !ch.destination.trim()
    );
    if (invalidChannels) {
      newErrors.notificationChannels = 'All notification channels must have a destination';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, validateConditionJson]);

  // Check name uniqueness
  const checkNameUniqueness = useCallback(async () => {
    if (!formData.name.trim()) return;

    setNameCheckPending(true);
    try {
      const result = await validateNameMutation.mutateAsync({
        name: formData.name,
        excludeId: editingRule?.id,
      });
      if (!result.isValid) {
        setErrors((prev) => ({ ...prev, name: result.message || 'This name is already in use' }));
      } else {
        setErrors((prev) => {
          const { name, ...rest } = prev;
          return rest;
        });
      }
    } catch {
      // Ignore validation errors
    } finally {
      setNameCheckPending(false);
    }
  }, [formData.name, editingRule?.id, validateNameMutation]);

  // Handle form submission
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      setIsSubmitting(true);
      setErrors({});

      try {
        const conditionJson = JSON.parse(formData.conditionJson) as ConditionJson;

        if (isEditing && editingRule) {
          // Update existing rule
          const updateData: UpdateAlertRuleRequest = {
            name: formData.name.trim(),
            alertType: formData.alertType,
            conditionJson,
            lakehouseId: formData.lakehouseId || null,
            enabled: formData.enabled,
            severityOverride: formData.severityOverride || null,
            notificationChannels:
              formData.notificationChannels.length > 0
                ? formData.notificationChannels
                : null,
            cooldownMinutes: formData.cooldownMinutes,
          };

          await updateMutation.mutateAsync({ id: editingRule.id, data: updateData });
        } else {
          // Create new rule
          const createData: CreateAlertRuleRequest = {
            name: formData.name.trim(),
            alertType: formData.alertType,
            conditionJson,
            lakehouseId: formData.lakehouseId || null,
            enabled: formData.enabled,
            severityOverride: formData.severityOverride || null,
            notificationChannels:
              formData.notificationChannels.length > 0
                ? formData.notificationChannels
                : null,
            cooldownMinutes: formData.cooldownMinutes,
          };

          await createMutation.mutateAsync(createData);
        }

        onSuccess();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred';
        setErrors({ general: message });
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      formData,
      isEditing,
      editingRule,
      validateForm,
      createMutation,
      updateMutation,
      onSuccess,
    ]
  );

  // Handle field changes
  const handleChange = useCallback(
    (field: keyof FormData, value: FormData[keyof FormData]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear field error on change
      if (errors[field as keyof FormErrors]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field as keyof FormErrors];
          return newErrors;
        });
      }
    },
    [errors]
  );

  // Lakehouse options
  const lakehouseOptions = useMemo((): { value: string; label: string }[] => {
    return lakehouses?.data?.map((lh: LakehouseWithWorkspace) => ({
      value: lh.id,
      label: lh.lakehouseName || lh.id,
    })) ?? [];
  }, [lakehouses]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Alert Rule' : 'Create Alert Rule'}
      size="md"
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="alert-rule-form"
            variant="primary"
            isLoading={isSubmitting}
            disabled={isSubmitting || nameCheckPending}
            leftIcon={!isSubmitting ? <Save className="w-4 h-4" /> : undefined}
          >
            {isSubmitting
              ? isEditing
                ? 'Updating...'
                : 'Creating...'
              : isEditing
                ? 'Update Rule'
                : 'Create Rule'}
          </Button>
        </>
      }
    >
      {/* Form */}
      <form id="alert-rule-form" onSubmit={handleSubmit} className="space-y-6">
            {/* General Error */}
            {errors.general && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-400">{errors.general}</p>
              </div>
            )}

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                Rule Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                onBlur={checkNameUniqueness}
                disabled={isSubmitting}
                className={`w-full px-3 py-2 border rounded-md shadow-sm bg-[var(--color-bg-card)] text-[var(--color-text-primary)] focus:ring-brand focus:border-brand ${
                  errors.name
                    ? 'border-red-300 dark:border-red-600'
                    : 'border-[var(--color-border)]'
                }`}
                placeholder="Backup Failure Alert"
              />
              {nameCheckPending && (
                <p className="mt-1 text-xs text-[var(--color-text-secondary)]">Checking name availability...</p>
              )}
              {errors.name && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>
              )}
            </div>

            {/* Resource Type (Spec 193 — selects the engine adapter) */}
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                Resource Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.resourceType}
                onChange={(e) => handleResourceTypeChange(e.target.value as ResourceType)}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md shadow-sm bg-[var(--color-bg-card)] text-[var(--color-text-primary)] focus:ring-brand focus:border-brand"
              >
                {RESOURCE_TYPE_OPTIONS.map((opt: ResourceTypeOption) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                {RESOURCE_TYPE_OPTIONS.find((o) => o.value === formData.resourceType)?.description ??
                  'The signal this rule evaluates.'}
                {' '}Targets: {(RESOURCE_TYPE_OPTIONS.find((o) => o.value === formData.resourceType)?.targetEntityTypes ?? []).join(', ') || 'any'}.
              </p>
            </div>

            {/* Alert Type label (free display label; defaults from the resource type) */}
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                Alert Type Label
              </label>
              <input
                type="text"
                value={formData.alertType}
                onChange={(e) => handleChange('alertType', e.target.value as AlertType)}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md shadow-sm bg-[var(--color-bg-card)] text-[var(--color-text-primary)] focus:ring-brand focus:border-brand"
                placeholder={deriveAlertType(formData.resourceType)}
              />
            </div>

            {/* Severity Override */}
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                Severity Override
              </label>
              <select
                value={formData.severityOverride}
                onChange={(e) => handleChange('severityOverride', e.target.value as Severity | '')}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md shadow-sm bg-[var(--color-bg-card)] text-[var(--color-text-primary)] focus:ring-brand focus:border-brand"
              >
                <option value="">Use default severity</option>
                {severities?.map((sev: SeverityOption) => (
                  <option key={sev.value} value={sev.value}>
                    {sev.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                Leave empty to use the default severity for this alert type
              </p>
            </div>

            {/* Lakehouse Scope */}
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                Lakehouse Scope
              </label>
              <select
                value={formData.lakehouseId}
                onChange={(e) => handleChange('lakehouseId', e.target.value)}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md shadow-sm bg-[var(--color-bg-card)] text-[var(--color-text-primary)] focus:ring-brand focus:border-brand"
              >
                <option value="">Global (all lakehouses)</option>
                {lakehouseOptions.map((lh) => (
                  <option key={lh.value} value={lh.value}>
                    {lh.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                Scope this rule to a specific lakehouse, or leave as Global to apply to all
              </p>
            </div>

            {/* Condition JSON */}
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                Condition <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.conditionJson}
                onChange={(e) => handleChange('conditionJson', e.target.value)}
                disabled={isSubmitting}
                rows={6}
                className={`w-full px-3 py-2 border rounded-md shadow-sm font-mono text-sm bg-[var(--color-bg-card)] text-[var(--color-text-primary)] focus:ring-brand focus:border-brand ${
                  errors.conditionJson
                    ? 'border-red-300 dark:border-red-600'
                    : 'border-[var(--color-border)]'
                }`}
                placeholder={JSON.stringify(conditionTemplateFor(formData.resourceType), null, 2)}
              />
              <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                Must include <code>resource_type</code> (set automatically). Add adapter-specific
                fields as needed, e.g. <code>{'"status": ["at_risk","blocked"]'}</code> for backup-readiness.
              </p>
              {errors.conditionJson && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.conditionJson}
                </p>
              )}
              <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                JSON object defining the trigger condition. Must include &quot;field&quot; and
                &quot;operator&quot; properties.
              </p>
            </div>

            {/* Cooldown Minutes */}
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                Cooldown (minutes)
              </label>
              <input
                type="number"
                min={1}
                max={1440}
                value={formData.cooldownMinutes}
                onChange={(e) =>
                  handleChange('cooldownMinutes', parseInt(e.target.value) || 60)
                }
                disabled={isSubmitting}
                className={`w-32 px-3 py-2 border rounded-md shadow-sm bg-[var(--color-bg-card)] text-[var(--color-text-primary)] focus:ring-brand focus:border-brand ${
                  errors.cooldownMinutes
                    ? 'border-red-300 dark:border-red-600'
                    : 'border-[var(--color-border)]'
                }`}
              />
              {errors.cooldownMinutes && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.cooldownMinutes}
                </p>
              )}
              <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                Minimum time between repeated alerts (1-1440 minutes)
              </p>
            </div>

            {/* Enabled Toggle */}
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={(e) => handleChange('enabled', e.target.checked)}
                  disabled={isSubmitting}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-[var(--color-border)] peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand/20 dark:peer-focus:ring-brand/40 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand" />
              </label>
              <span className="text-sm font-medium text-[var(--color-text-secondary)]">
                {formData.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>

            {/* Notification Channels */}
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                Notification Channels
              </label>
              <NotificationChannelEditor
                channels={formData.notificationChannels}
                onChange={(channels) => handleChange('notificationChannels', channels)}
                disabled={isSubmitting}
                error={errors.notificationChannels}
              />
            </div>
      </form>
    </Modal>
  );
};

export default AlertRuleForm;
